import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/magazzino/lib/apiClient";
import { useAppearance, DEFAULT_APPEARANCE } from "@/magazzino/lib/appearance";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Buildings, FloppyDisk, Image as ImageIcon, PaintBrush, ArrowCounterClockwise } from "@phosphor-icons/react";

const empty = { name: "", address: "", city: "", vat: "", phone: "", email: "", logo_url: "" };
const colors = ["#0f172a", "#1d4ed8", "#047857", "#7c3aed", "#b45309", "#be123c"];

export default function SettingsPage() {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const { appearance, setAppearance, resetAppearance } = useAppearance();

  useEffect(() => {
    api.get("/settings/company").then(({ data }) => setForm({ ...empty, ...data }));
  }, []);

  const onLogoFile = async (file) => {
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Logo troppo grande (max 500KB). Usa un'immagine più leggera.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/company", form);
      toast.success("Impostazioni aziendali salvate");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const updateAppearance = (patch) => setAppearance((current) => ({ ...current, ...patch }));

  return (
    <div className="max-w-4xl space-y-6 lg:h-full lg:overflow-y-auto lg:pr-2">
      <Card className="border-slate-200 rounded-md shadow-none p-6" data-testid="appearance-settings-form">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 text-white grid place-items-center rounded-md" style={{ backgroundColor: appearance.primaryColor }}>
              <PaintBrush weight="bold" size={20}/>
            </div>
            <div>
              <div className="font-display text-lg font-bold tracking-tight">Personalizzazione grafica</div>
              <div className="text-xs text-slate-500">Le modifiche vengono applicate subito e salvate su questo dispositivo</div>
            </div>
          </div>
          <Button variant="outline" onClick={() => { resetAppearance(); toast.success("Grafica ripristinata"); }}>
            <ArrowCounterClockwise weight="bold" size={16} className="mr-1.5"/> Ripristina
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label>Nome del gestionale</Label>
            <Input className="mt-1" value={appearance.appName} onChange={(e) => updateAppearance({ appName: e.target.value })}/>
          </div>
          <div>
            <Label>Sottotitolo</Label>
            <Input className="mt-1" value={appearance.subtitle} onChange={(e) => updateAppearance({ subtitle: e.target.value })}/>
          </div>

          <div className="sm:col-span-2">
            <Label>Colore principale</Label>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              {colors.map((color) => (
                <button
                  key={color}
                  type="button"
                  aria-label={`Seleziona colore ${color}`}
                  onClick={() => updateAppearance({ primaryColor: color })}
                  className={`w-9 h-9 rounded-full border-2 ${appearance.primaryColor === color ? "ring-2 ring-offset-2 ring-slate-400" : "border-white"}`}
                  style={{ backgroundColor: color }}
                />
              ))}
              <Input type="color" value={appearance.primaryColor} onChange={(e) => updateAppearance({ primaryColor: e.target.value })} className="w-14 h-10 p-1 cursor-pointer"/>
              <span className="text-sm tabular text-slate-500">{appearance.primaryColor}</span>
            </div>
          </div>

          <div>
            <Label>Stile del menu laterale</Label>
            <Select value={appearance.sidebarStyle} onValueChange={(value) => updateAppearance({ sidebarStyle: value })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="light">Chiaro</SelectItem>
                <SelectItem value="dark">Scuro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Spaziatura delle schermate</Label>
            <Select value={appearance.density} onValueChange={(value) => updateAppearance({ density: value })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="compact">Compatta</SelectItem>
                <SelectItem value="comfortable">Normale</SelectItem>
                <SelectItem value="relaxed">Ampia</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Arrotondamento</Label>
            <Select value={appearance.radius} onValueChange={(value) => updateAppearance({ radius: value })}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="small">Minimo</SelectItem>
                <SelectItem value="medium">Normale</SelectItem>
                <SelectItem value="large">Ampio</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between rounded-md border border-slate-200 px-4 py-3">
            <div>
              <Label htmlFor="show-subtitle">Mostra sottotitolo</Label>
              <div className="text-xs text-slate-500">Visualizzato sotto il nome nel menu</div>
            </div>
            <Switch id="show-subtitle" checked={appearance.showSubtitle} onCheckedChange={(value) => updateAppearance({ showSubtitle: value })}/>
          </div>
        </div>

        <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Anteprima</div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md text-white grid place-items-center" style={{ backgroundColor: appearance.primaryColor }}>
              <PaintBrush weight="bold" size={19}/>
            </div>
            <div>
              <div className="font-display font-bold">{appearance.appName || DEFAULT_APPEARANCE.appName}</div>
              {appearance.showSubtitle && <div className="text-xs text-slate-500">{appearance.subtitle || DEFAULT_APPEARANCE.subtitle}</div>}
            </div>
            <button className="ml-auto px-4 py-2 text-sm text-white rounded-md" style={{ backgroundColor: appearance.primaryColor }}>Pulsante esempio</button>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 rounded-md shadow-none p-6" data-testid="company-settings-form">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-slate-900 text-white grid place-items-center rounded-md">
            <Buildings weight="bold" size={20}/>
          </div>
          <div>
            <div className="font-display text-lg font-bold tracking-tight">Dati aziendali</div>
            <div className="text-xs text-slate-500">Compariranno sulla bolla di carico/scarico (DDT)</div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-slate-600">Ragione sociale *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1"/></div>
          <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-slate-600">Indirizzo</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1"/></div>
          <div><Label className="text-xs uppercase tracking-wider text-slate-600">Città / CAP</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1"/></div>
          <div><Label className="text-xs uppercase tracking-wider text-slate-600">P.IVA / C.F.</Label><Input value={form.vat} onChange={(e) => setForm({ ...form, vat: e.target.value })} className="mt-1"/></div>
          <div><Label className="text-xs uppercase tracking-wider text-slate-600">Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1"/></div>
          <div><Label className="text-xs uppercase tracking-wider text-slate-600">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1"/></div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-slate-600">Logo aziendale</Label>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-24 h-24 border border-slate-200 rounded-md grid place-items-center bg-slate-50 overflow-hidden">
                {form.logo_url ? <img src={form.logo_url} alt="logo" className="max-w-full max-h-full object-contain"/> : <ImageIcon size={24} className="text-slate-300"/>}
              </div>
              <div className="flex-1 space-y-2">
                <Input type="file" accept="image/*" onChange={(e) => onLogoFile(e.target.files?.[0])} className="cursor-pointer"/>
                <Input placeholder="…oppure incolla un URL" value={form.logo_url?.startsWith("data:") ? "" : form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })}/>
                {form.logo_url && <button type="button" onClick={() => setForm({ ...form, logo_url: "" })} className="text-xs text-red-600 hover:underline">Rimuovi logo</button>}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={save} disabled={saving} className="h-10 text-white" style={{ backgroundColor: appearance.primaryColor }}>
            <FloppyDisk weight="bold" size={16} className="mr-1.5"/>{saving ? "Salvataggio…" : "Salva impostazioni"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
